import { useRef, useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import {
  ArrowRight, Megaphone, X,
  Fuel, Flame, Droplets, Atom, Wrench, Wheat, Zap, Package,
  ShoppingCart, FileText, LogOut, ChevronDown, ChevronUp,
  Check, Minus, Plus, TrendingUp, TrendingDown, DollarSign,
  RefreshCw, Trash2, Edit3, AlertCircle, ClipboardList,
  Eye, EyeOff, ToggleLeft, ToggleRight, Settings,
  Hammer, Pickaxe, Factory, Truck, Anchor, Shield, Sword,
  Crosshair, Target, MapPin, Globe
} from 'lucide-react';
import { announcementsAPI, statsAPI, homeProductsAPI } from '../api';

const BADGE_META = {
  news:     { label: 'NEWS',      bg: 'rgba(100,100,255,0.12)', border: 'rgba(100,100,255,0.3)',  text: '#8888ff' },
  restock:  { label: 'RESTOCK',   bg: 'rgba(34,197,94,0.1)',   border: 'rgba(34,197,94,0.3)',    text: '#22c55e' },
  discount: { label: 'DISCOUNT',  bg: 'rgba(245,168,0,0.12)',  border: 'rgba(245,168,0,0.35)',   text: '#F5A800' },
  storm:    { label: 'STORM SALE',bg: 'rgba(239,68,68,0.1)',   border: 'rgba(239,68,68,0.3)',    text: '#ef4444' },
  alert:    { label: 'ALERT',     bg: 'rgba(245,168,0,0.08)',  border: 'rgba(245,168,0,0.25)',   text: '#F5A800' },
};

// Map icon names to Lucide components
const ICON_MAP = {
  Fuel, Flame, Droplets, Atom, Wrench, Wheat, Zap, Package,
  ShoppingCart, FileText, LogOut, ChevronDown, ChevronUp,
  Check, Minus, Plus, TrendingUp, TrendingDown, DollarSign,
  RefreshCw, Trash2, Edit3, AlertCircle, ClipboardList,
  Eye, EyeOff, ToggleLeft, ToggleRight, Settings,
  Hammer, Pickaxe, Factory, Truck, Anchor, Shield, Sword,
  Crosshair, Target, MapPin, Globe
};

/* ─── Counter ─────────────────────────────────────────────────────────────── */
function Counter({ to, duration = 1.8 }) {
  const [val, setVal] = useState(0);
  const ref = useRef(null);
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => {
      if (!e.isIntersecting) return;
      obs.disconnect();
      const start = Date.now();
      const tick = () => {
        const p = Math.min((Date.now() - start) / (duration * 1000), 1);
        setVal(Math.floor(p * to));
        if (p < 1) requestAnimationFrame(tick);
      };
      requestAnimationFrame(tick);
    });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [to, duration]);
  return <span ref={ref}>{val}</span>;
}

/* ─── Pipeline lines ──────────────────────────────────────────────────────── */
function DarkPipeLines() {
  const lines = [
    { y: 12, dur: 5.0, delay: 0.0, h: 1 },
    { y: 26, dur: 3.8, delay: 0.7, h: 2 },
    { y: 41, dur: 6.2, delay: 0.3, h: 1 },
    { y: 57, dur: 4.4, delay: 1.1, h: 1 },
    { y: 72, dur: 5.6, delay: 0.5, h: 2 },
    { y: 87, dur: 3.5, delay: 0.9, h: 1 },
  ];
  return (
    <div style={{ position: 'absolute', inset: 0, overflow: 'hidden', pointerEvents: 'none' }}>
      {lines.map((l, i) => (
        <motion.div key={i}
          style={{
            position: 'absolute',
            top: `${l.y}%`,
            height: l.h,
            width: '60%',
            left: '-60%',
          }}
          animate={{
            x: ['0%', '280%'],
            backgroundColor: [
              'rgba(245,168,0,0.45)',
              'rgba(255,255,255,0.35)',
              'rgba(245,168,0,0.55)',
              'rgba(255,255,255,0.2)',
              'rgba(245,168,0,0.45)',
            ],
          }}
          transition={{
            x:               { duration: l.dur, repeat: Infinity, ease: 'linear', delay: l.delay },
            backgroundColor: { duration: l.dur * 0.6, repeat: Infinity, ease: 'easeInOut', delay: l.delay },
          }}
        />
      ))}
    </div>
  );
}

function GoldPipeLines() {
  const lines = [
    { y: 18, dur: 4.2, delay: 0.2 },
    { y: 35, dur: 5.5, delay: 0.8 },
    { y: 50, dur: 3.6, delay: 0.0 },
    { y: 65, dur: 6.0, delay: 1.2 },
    { y: 80, dur: 4.8, delay: 0.5 },
  ];
  return (
    <div style={{ position: 'absolute', inset: 0, overflow: 'hidden', pointerEvents: 'none' }}>
      {lines.map((l, i) => (
        <motion.div key={i}
          style={{
            position: 'absolute',
            top: `${l.y}%`,
            height: 1,
            width: '80%',
            left: '-80%',
            background: 'rgba(0,0,0,0.18)',
          }}
          animate={{ x: ['0%', '220%'] }}
          transition={{ duration: l.dur, repeat: Infinity, ease: 'linear', delay: l.delay }}
        />
      ))}
    </div>
  );
}

/* ─── Manifest row ────────────────────────────────────────────────────────── */
function ManifestRow({ name, price, iconName, index, delay = 0 }) {
  const IconComp = ICON_MAP[iconName] || Package;
  return (
    <motion.div
      initial={{ opacity: 0, x: -20 }}
      whileInView={{ opacity: 1, x: 0 }}
      viewport={{ once: true, margin: '-40px' }}
      transition={{ delay: delay + index * 0.07, duration: 0.4 }}
      whileHover={{ x: 6 }}
      style={{
        display: 'flex', alignItems: 'center',
        padding: '1.1rem 0',
        borderBottom: '1px solid rgba(245,168,0,0.1)',
        gap: '1rem',
      }}
    >
      <span style={{ fontFamily: 'monospace', fontSize: 10, color: 'rgba(245,168,0,0.22)', minWidth: 26 }}>
        {String(index + 1).padStart(2, '0')}
      </span>
      <IconComp size={18} color="var(--bce-gold)" style={{ opacity: 0.7, flexShrink: 0 }} />
      <span style={{
        fontFamily: 'var(--font-condensed)', fontWeight: 700,
        fontSize: 'clamp(13px, 1.8vw, 17px)',
        letterSpacing: '0.1em', textTransform: 'uppercase',
        color: 'var(--bce-white)', flex: 1,
      }}>
        {name}
      </span>
      <div style={{ flex: 2, borderBottom: '1px dashed rgba(245,168,0,0.15)', height: 1, minWidth: 30 }} />
      <span style={{
        fontFamily: 'var(--font-display)',
        fontSize: 'clamp(1.8rem, 4vw, 3.2rem)',
        color: 'var(--bce-gold)', lineHeight: 1,
        minWidth: 110, textAlign: 'right',
      }}>
        ${price.toLocaleString()}
      </span>
    </motion.div>
  );
}

/* ─── Section header ──────────────────────────────────────────────────────── */
function SectionHead({ white, gold }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 22 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-60px' }}
      transition={{ duration: 0.55, ease: [0.16, 1, 0.3, 1] }}
      style={{ display: 'flex', alignItems: 'baseline', gap: '1rem', marginBottom: '2.5rem' }}
    >
      <span style={{ fontFamily: 'var(--font-condensed)', fontSize: 13, letterSpacing: '0.2em', color: 'rgba(245,168,0,0.45)', alignSelf: 'center' }}>/</span>
      <span style={{ fontFamily: 'var(--font-display)', fontSize: 'clamp(2.5rem, 6vw, 5rem)', color: 'var(--bce-white)', lineHeight: 1 }}>{white}</span>
      <span style={{ fontFamily: 'var(--font-display)', fontSize: 'clamp(2.5rem, 6vw, 5rem)', color: 'var(--bce-gold)',  lineHeight: 1 }}>{gold}</span>
      <motion.div
        initial={{ scaleX: 0 }}
        whileInView={{ scaleX: 1 }}
        viewport={{ once: true, margin: '-60px' }}
        transition={{ duration: 0.6, delay: 0.25, ease: 'easeOut' }}
        style={{ flex: 1, height: 1, background: 'linear-gradient(to right, rgba(245,168,0,0.3), transparent)', alignSelf: 'center', transformOrigin: 'left' }}
      />
    </motion.div>
  );
}

/* ─── Announcements banner ────────────────────────────────────────────────── */
function AnnouncementsBanner() {
  const [items, setItems]     = useState([]);
  const [dismissed, setDismissed] = useState(new Set());

  useEffect(() => {
    announcementsAPI.list()
      .then(r => setItems(r.data))
      .catch(() => {});
  }, []);

  const visible = items.filter(a => !dismissed.has(a.id));
  if (visible.length === 0) return null;

  return (
    <section style={{ background: 'var(--bce-bg-card)', borderBottom: '1px solid var(--bce-border)' }}>
      <div className="bce-container" style={{ paddingTop: '2.5rem', paddingBottom: '2.5rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: '1.25rem' }}>
          <Megaphone size={14} color="var(--bce-gold)" />
          <span style={{ fontFamily: 'var(--font-condensed)', fontSize: 11, letterSpacing: '0.2em', textTransform: 'uppercase', color: 'var(--bce-gold)' }}>
            Announcements
          </span>
          <div style={{ flex: 1, height: 1, background: 'linear-gradient(to right, rgba(245,168,0,0.25), transparent)' }} />
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <AnimatePresence>
            {visible.map(ann => {
              const meta = BADGE_META[ann.badge] || BADGE_META.news;
              return (
                <motion.div
                  key={ann.id}
                  initial={{ opacity: 0, x: -12 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, height: 0, marginBottom: 0 }}
                  transition={{ duration: 0.25 }}
                  style={{
                    display: 'flex', alignItems: 'flex-start', gap: 12,
                    padding: '0.9rem 1.25rem',
                    background: meta.bg,
                    border: `1px solid ${meta.border}`,
                  }}
                >
                  <span style={{
                    fontSize: 8, fontFamily: 'var(--font-condensed)', fontWeight: 700,
                    letterSpacing: '0.15em', textTransform: 'uppercase',
                    color: meta.text, background: `${meta.border}`,
                    border: `1px solid ${meta.border}`,
                    padding: '2px 7px', flexShrink: 0, marginTop: 2,
                  }}>
                    {meta.label}
                  </span>
                  <div style={{ flex: 1 }}>
                    <div style={{
                      fontFamily: 'var(--font-condensed)', fontWeight: 700,
                      fontSize: 14, letterSpacing: '0.06em', textTransform: 'uppercase',
                      color: 'var(--bce-white)', marginBottom: ann.body ? 3 : 0,
                    }}>
                      {ann.title}
                    </div>
                    {ann.body && (
                      <div style={{ fontSize: 13, color: 'var(--bce-text)', lineHeight: 1.6 }}>
                        {ann.body}
                      </div>
                    )}
                  </div>
                  <button
                    onClick={() => setDismissed(d => new Set([...d, ann.id]))}
                    style={{
                      background: 'transparent', border: 'none', cursor: 'pointer',
                      color: 'var(--bce-text-faint)', padding: 4, flexShrink: 0,
                      display: 'flex', alignItems: 'center',
                    }}
                  >
                    <X size={13} />
                  </button>
                </motion.div>
              );
            })}
          </AnimatePresence>
        </div>
      </div>
    </section>
  );
}

/* ─── Marquee items builder ──────────────────────────────────────────────── */
function MarqueeStrip({ items }) {
  const displayItems = items.length > 0 ? items : [
    'JERRY CANS · $1,000', 'ETHANOL · $250', 'REFINED OIL · $250',
    'NITRATE · $80', 'BC FREEDOM LUBE · $1,000', '◆'
  ];

  const allItems = [];
  for (let ri = 0; ri < 4; ri++) {
    displayItems.forEach((t, i) => {
      allItems.push({ key: `${ri}-${i}`, text: t });
    });
    allItems.push({ key: `${ri}-sep`, text: '◆' });
  }

  return (
    <div style={{ background: 'var(--bce-gold)', borderTop: '3px solid #000', borderBottom: '3px solid #000' }}>
      <div style={{ overflow: 'hidden', padding: '11px 0' }}>
        <div style={{ display: 'flex', animation: 'bce-marquee 22s linear infinite', width: 'max-content' }}>
          {allItems.map(item => (
            <span key={item.key} style={{
              fontFamily: 'var(--font-display)', fontSize: '0.95rem',
              letterSpacing: '0.14em', color: '#000',
              padding: '0 2rem', whiteSpace: 'nowrap',
            }}>
              {item.text}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ─── HOME ────────────────────────────────────────────────────────────────── */
export default function Home() {
  const [productsHandled, setProductsHandled] = useState(0);
  const [homeProducts, setHomeProducts] = useState([]);
  const [productsLoading, setProductsLoading] = useState(true);

  useEffect(() => {
    statsAPI.get()
      .then(r => setProductsHandled(r.data.products_handled))
      .catch(() => {});

    homeProductsAPI.list()
      .then(r => setHomeProducts(r.data))
      .catch(() => {})
      .finally(() => setProductsLoading(false));
  }, []);

  const buyGoods  = homeProducts.filter(p => p.category === 'buy').sort((a, b) => a.sort_order - b.sort_order);
  const sellGoods = homeProducts.filter(p => p.category === 'sell').sort((a, b) => a.sort_order - b.sort_order);

  // Build marquee items from buy goods
  const marqueeItems = buyGoods.map(p => `${p.name.toUpperCase()} · $${p.price.toLocaleString()}`);

  return (
    <div style={{ background: 'var(--bce-bg)', overflowX: 'hidden' }}>

      {/* ══ HERO — DIAGONAL SPLIT ══════════════════════════════════════════ */}
      <section style={{ height: '100vh', position: 'relative', overflow: 'hidden' }}>

        {/* Dark left background */}
        <div style={{
          position: 'absolute', inset: 0, zIndex: 0,
          background: '#080808',
        }} />

        {/* Gold right panel — diagonal clip */}
        <div style={{
          position: 'absolute', inset: 0, zIndex: 1,
          background: 'var(--bce-gold)',
          clipPath: 'polygon(63% 0, 100% 0, 100% 100%, 53% 100%)',
        }}>
          <GoldPipeLines />
        </div>

        {/* Pipeline lines on dark side */}
        <div style={{
          position: 'absolute', inset: 0, zIndex: 2,
          clipPath: 'polygon(0 0, 63% 0, 53% 100%, 0 100%)',
          overflow: 'hidden',
        }}>
          <DarkPipeLines />
        </div>

        {/* Vertical "ENERGY" at the split boundary */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.9, duration: 0.7 }}
          style={{
            position: 'absolute',
            left: '58%',
            top: '50%',
            transform: 'translate(-50%, -50%) rotate(-90deg)',
            zIndex: 10,
            fontFamily: 'var(--font-display)',
            fontSize: 'clamp(3.5rem, 6vw, 6.5rem)',
            letterSpacing: '0.18em',
            whiteSpace: 'nowrap',
            color: 'var(--bce-gold)',
            mixBlendMode: 'difference',
            pointerEvents: 'none',
          }}
        >
          ENERGY
        </motion.div>

        {/* ── LEFT content: POWERING TODAY ── */}
        <div style={{
          position: 'absolute', zIndex: 5,
          left: 0, top: 0, bottom: 0,
          width: '57%',
          display: 'flex', flexDirection: 'column', justifyContent: 'flex-end',
          padding: '0 4rem 5rem 2rem',
        }}>
          <motion.div initial="hidden" animate="show"
            variants={{ hidden: {}, show: { transition: { staggerChildren: 0.09 } } }}>

            <motion.div
              variants={{ hidden: { opacity: 0, x: -14 }, show: { opacity: 1, x: 0 } }}
              style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: '1.25rem' }}
            >
              <div style={{ width: 36, height: 1, background: 'var(--bce-gold)' }} />
              <span style={{ fontFamily: 'var(--font-condensed)', fontSize: 10, letterSpacing: '0.28em', color: 'var(--bce-gold)', textTransform: 'uppercase' }}>
                Big Country · Energy Division
              </span>
            </motion.div>

            <div style={{ overflow: 'hidden' }}>
              <motion.h1
                variants={{ hidden: { y: '105%' }, show: { y: 0, transition: { duration: 0.7, ease: [0.16, 1, 0.3, 1] } } }}
                style={{ fontFamily: 'var(--font-display)', fontSize: 'clamp(4rem, 10vw, 9rem)', color: 'var(--bce-white)', lineHeight: 0.88, margin: 0 }}
              >
                POWERING
              </motion.h1>
            </div>
            <div style={{ overflow: 'hidden' }}>
              <motion.h1
                variants={{ hidden: { y: '105%' }, show: { y: 0, transition: { duration: 0.7, ease: [0.16, 1, 0.3, 1] } } }}
                style={{ fontFamily: 'var(--font-display)', fontSize: 'clamp(4rem, 10vw, 9rem)', color: 'var(--bce-white)', lineHeight: 0.88, marginBottom: '2rem' }}
              >
                TODAY.
              </motion.h1>
            </div>

            <motion.div
              variants={{ hidden: { scaleX: 0 }, show: { scaleX: 1, transition: { duration: 0.5, ease: 'easeOut', originX: 0 } } }}
              style={{ height: 2, background: 'linear-gradient(to right, var(--bce-gold), transparent)', maxWidth: 380, marginBottom: '1.5rem' }}
            />

            <motion.div
              variants={{ hidden: { opacity: 0 }, show: { opacity: 1 } }}
              style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}
            >
              <p style={{ color: 'var(--bce-text)', fontSize: 13, maxWidth: 240, lineHeight: 1.6 }}>
                Reliable supply. Fair prices. Big Country.
              </p>
              <div style={{ display: 'flex', gap: 8 }}>
                <Link to="/order" className="bce-btn bce-btn-gold">Place Order <ArrowRight size={14} /></Link>
                <Link to="/apply" className="bce-btn bce-btn-outline">Join Team</Link>
              </div>
            </motion.div>
          </motion.div>
        </div>

        {/* ── RIGHT content: FUELING TOMORROW on gold ── */}
        <motion.div
          style={{
            position: 'absolute', zIndex: 5,
            right: 0, top: 0, bottom: 0,
            width: '40%',
            display: 'flex', flexDirection: 'column', justifyContent: 'flex-end',
            padding: '0 2rem 5rem 6rem',
          }}
        >
          <div style={{ overflow: 'hidden' }}>
            <motion.h1
              initial={{ y: '105%' }} animate={{ y: 0 }}
              transition={{ delay: 0.25, duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
              style={{ fontFamily: 'var(--font-display)', fontSize: 'clamp(4rem, 10vw, 9rem)', color: '#000', lineHeight: 0.88, margin: 0 }}
            >
              FUELING
            </motion.h1>
          </div>
          <div style={{ overflow: 'hidden' }}>
            <motion.h1
              initial={{ y: '105%' }} animate={{ y: 0 }}
              transition={{ delay: 0.34, duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
              style={{ fontFamily: 'var(--font-display)', fontSize: 'clamp(4rem, 10vw, 9rem)', color: '#000', lineHeight: 0.88, marginBottom: '2rem' }}
            >
              TOMORROW.
            </motion.h1>
          </div>
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.7 }}>
            <div style={{ width: 36, height: 2, background: 'rgba(0,0,0,0.3)', marginBottom: '0.75rem' }} />
            <p style={{ fontFamily: 'var(--font-condensed)', fontSize: 11, letterSpacing: '0.2em', color: 'rgba(0,0,0,0.45)', textTransform: 'uppercase' }}>
              Bigger. Stronger. United.
            </p>
          </motion.div>
        </motion.div>
      </section>

      {/* ══ MARQUEE — dynamic from API ═══════════════════════════════════ */}
      <MarqueeStrip items={marqueeItems} />

      {/* ══ ANNOUNCEMENTS ══════════════════════════════════════════════════ */}
      <AnnouncementsBanner />

      {/* ══ BUY GOODS ══════════════════════════════════════════════════════ */}
      <motion.section
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true, margin: '-80px' }}
        transition={{ duration: 0.5 }}
        style={{ background: 'var(--bce-bg)', padding: '6rem 0 5rem' }}
      >
        <div className="bce-container">
          <SectionHead white="BUY" gold="GOODS" />
          <div style={{ borderTop: '2px solid var(--bce-gold)', marginBottom: '2rem' }}>
            {productsLoading ? (
              <div style={{ padding: '2rem 0', textAlign: 'center', color: 'var(--bce-text-faint)' }}>
                <div className="bce-spinner" style={{ margin: '0 auto 1rem' }} />
                <span style={{ fontFamily: 'var(--font-condensed)', fontSize: 12, letterSpacing: '0.1em', textTransform: 'uppercase' }}>Loading products...</span>
              </div>
            ) : buyGoods.length === 0 ? (
              <div style={{ padding: '2rem 0', color: 'var(--bce-text-faint)', fontSize: 13 }}>No products listed.</div>
            ) : (
              buyGoods.map((p, i) => <ManifestRow key={p.id} name={p.name} price={p.price} iconName={p.icon} index={i} />)
            )}
            <div style={{ padding: '0.75rem 0', display: 'flex', justifyContent: 'flex-end' }}>
              <span style={{ fontFamily: 'var(--font-condensed)', fontSize: 10, letterSpacing: '0.15em', color: 'var(--bce-text-faint)', textTransform: 'uppercase' }}>
                All prices per unit · Subject to availability
              </span>
            </div>
          </div>
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.4, duration: 0.4 }}
          >
            <Link to="/order" className="bce-btn bce-btn-gold">
              Pre-Order Now <ArrowRight size={14} />
            </Link>
          </motion.div>
        </div>
      </motion.section>

      {/* ══ SELL GOODS ═════════════════════════════════════════════════════ */}
      <motion.section
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true, margin: '-80px' }}
        transition={{ duration: 0.5 }}
        style={{ background: 'var(--bce-bg-card)', borderTop: '1px solid var(--bce-border)', padding: '6rem 0 5rem' }}
      >
        <div className="bce-container">
          <SectionHead white="SELL" gold="GOODS" />
          <motion.p
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2, duration: 0.45 }}
            style={{ color: 'var(--bce-text)', fontSize: 14, lineHeight: 1.7, marginBottom: '2rem', maxWidth: 460 }}
          >
            We purchase raw materials directly from suppliers. Bring your goods and we pay on the spot.
          </motion.p>
          <div style={{ borderTop: '2px solid var(--bce-gold)' }}>
            {productsLoading ? (
              <div style={{ padding: '2rem 0', textAlign: 'center', color: 'var(--bce-text-faint)' }}>
                <div className="bce-spinner" style={{ margin: '0 auto 1rem' }} />
                <span style={{ fontFamily: 'var(--font-condensed)', fontSize: 12, letterSpacing: '0.1em', textTransform: 'uppercase' }}>Loading products...</span>
              </div>
            ) : sellGoods.length === 0 ? (
              <div style={{ padding: '2rem 0', color: 'var(--bce-text-faint)', fontSize: 13 }}>No products listed.</div>
            ) : (
              sellGoods.map((p, i) => <ManifestRow key={p.id} name={p.name} price={p.price} iconName={p.icon} index={i} delay={0.1} />)
            )}
          </div>
        </div>
      </motion.section>

      {/* ══ STATS ══════════════════════════════════════════════════════════ */}
      <section style={{ background: 'var(--bce-bg)', borderTop: '1px solid var(--bce-border)', padding: '4rem 0' }}>
        <div className="bce-container">
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '1px', background: 'var(--bce-border)' }}>
            {[
              { val: productsHandled, suffix: '+',  label: 'Items Handled'     },
              { val: 24,              suffix: '/7',  label: 'Hour Operations'   },
              { val: 100,             suffix: '%',   label: 'Reliability Rate'  },
            ].map(({ val, suffix, label }) => (
              <motion.div key={label}
                initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }}
                style={{ background: 'var(--bce-bg-card)', padding: '2.5rem', textAlign: 'center' }}
              >
                <div style={{ fontFamily: 'var(--font-display)', fontSize: 'clamp(3rem, 6vw, 5rem)', color: 'var(--bce-gold)', lineHeight: 1 }}>
                  <Counter to={val} />{suffix}
                </div>
                <div style={{ fontFamily: 'var(--font-condensed)', fontSize: 10, letterSpacing: '0.18em', textTransform: 'uppercase', color: 'var(--bce-text-faint)', marginTop: '0.5rem' }}>
                  {label}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ══ CONTACTS ═══════════════════════════════════════════════════════ */}
      <section style={{ background: 'var(--bce-bg)', padding: '5rem 0' }}>
        <div className="bce-container">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-60px' }}
            transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
            style={{ display: 'flex', alignItems: 'baseline', gap: '1rem', marginBottom: '2rem' }}
          >
            <span style={{ fontFamily: 'var(--font-display)', fontSize: 'clamp(2rem, 5vw, 4rem)', color: 'var(--bce-white)' }}>CONTACTS</span>
            <div style={{ flex: 1, height: 1, background: 'linear-gradient(to right, rgba(245,168,0,0.3), transparent)' }} />
          </motion.div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '1px', background: 'var(--bce-border)' }}>
            {[
              { name: 'EDDIE', phone: '077-2152', role: 'Owner' },
              { name: 'Chris',  phone: '701-4569', role: 'Owner' },
            ].map((c, i) => (
              <motion.div key={c.name}
                initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }}
                style={{
                  background: 'var(--bce-bg-card)', padding: '2.5rem',
                  display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between',
                  borderLeft: '4px solid var(--bce-gold)',
                }}
              >
                <div>
                  <div style={{ fontFamily: 'var(--font-condensed)', fontSize: 10, letterSpacing: '0.2em', color: 'var(--bce-text-faint)', textTransform: 'uppercase', marginBottom: 8 }}>
                    {c.role}
                  </div>
                  <div style={{ fontFamily: 'var(--font-display)', fontSize: 'clamp(2.5rem, 6vw, 4.5rem)', color: 'var(--bce-white)', lineHeight: 1 }}>
                    {c.name}
                  </div>
                </div>
                <div style={{ fontFamily: 'var(--font-display)', fontSize: 'clamp(1.5rem, 3vw, 2.5rem)', color: 'var(--bce-gold)' }}>
                  {c.phone}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ══ CTA HAZARD ═════════════════════════════════════════════════════ */}
      <section>
        <div style={{ height: 14, backgroundImage: 'repeating-linear-gradient(-45deg, #F5A800 0, #F5A800 10px, #000 10px, #000 20px)' }} />
        <div style={{ background: 'var(--bce-gold)', padding: '5rem 2rem', textAlign: 'center' }}>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-40px' }}
            transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
          >
            <div style={{ fontFamily: 'var(--font-condensed)', fontSize: 10, letterSpacing: '0.25em', color: 'rgba(0,0,0,0.4)', textTransform: 'uppercase', marginBottom: '0.75rem' }}>
              Big Country Energy
            </div>
            <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 'clamp(2.5rem, 8vw, 6.5rem)', color: '#000', lineHeight: 0.9, marginBottom: '2.5rem' }}>
              READY TO FUEL<br />YOUR OPERATION?
            </h2>
            <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}>
              <Link to="/order" className="bce-btn" style={{ background: '#000', color: 'var(--bce-gold)', fontFamily: 'var(--font-condensed)', fontWeight: 700, letterSpacing: '0.12em' }}>
                Place an Order <ArrowRight size={14} />
              </Link>
              <Link to="/apply" className="bce-btn" style={{ background: 'transparent', color: '#000', border: '2px solid #000', fontFamily: 'var(--font-condensed)', fontWeight: 700, letterSpacing: '0.12em' }}>
                Join the Team
              </Link>
            </div>
          </motion.div>
        </div>
        <div style={{ height: 14, backgroundImage: 'repeating-linear-gradient(-45deg, #F5A800 0, #F5A800 10px, #000 10px, #000 20px)' }} />
      </section>

    </div>
  );
}
